import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const PASSWORD_RULES = [
  { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'upper', label: 'At least one uppercase letter (A-Z)', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'At least one lowercase letter (a-z)', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'At least one number (0-9)', test: (p) => /[0-9]/.test(p) },
  { id: 'special', label: 'At least one special character (!@#$%^&*...)', test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p) },
  { id: 'max', label: 'Maximum 64 characters', test: (p) => p.length <= 64 },
];

function ChangePassword() {
  const { updateUserAfterPasswordChange, user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  const allRulesPassed = PASSWORD_RULES.every((r) => r.test(newPassword));
  const passwordsMatch = newPassword && confirmPassword && newPassword === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!currentPassword) {
      setError('Please enter your current one-time password');
      return;
    }
    if (!allRulesPassed) {
      setError('New password does not meet all requirements');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/change-password', { currentPassword, newPassword });
      if (response.data.success) {
        updateUserAfterPasswordChange(response.data.token, response.data.user);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card" style={{ maxWidth: 480 }}>
        <div className="login-header">
          <h1>QPOSM</h1>
          <p>Change Your Password</p>
        </div>

        <div className="change-password-notice">
          Welcome, <strong>{user?.fullName}</strong>! You are required to change your password before continuing. Please set a strong password.
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Current One-Time Password</label>
            <div className="password-input-wrapper">
              <input
                type={showCurrent ? 'text' : 'password'}
                placeholder="Enter the one-time password you received"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus
              />
              <button type="button" className="password-toggle" onClick={() => setShowCurrent(!showCurrent)}>
                {showCurrent ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>New Password</label>
            <div className="password-input-wrapper">
              <input
                type={showNew ? 'text' : 'password'}
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
              />
              <button type="button" className="password-toggle" onClick={() => setShowNew(!showNew)}>
                {showNew ? '🙈' : '👁'}
              </button>
            </div>
          </div>

          {/* Password Rules Checklist */}
          {newPassword && (
            <div className="password-rules">
              {PASSWORD_RULES.map((rule) => (
                <div key={rule.id} className={`password-rule ${rule.test(newPassword) ? 'passed' : 'failed'}`}>
                  <span className="rule-icon">{rule.test(newPassword) ? '✓' : '✗'}</span>
                  {rule.label}
                </div>
              ))}
            </div>
          )}

          <div className="form-group">
            <label>Confirm New Password</label>
            <input
              type="password"
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
            {confirmPassword && !passwordsMatch && (
              <span style={{ color: '#c62828', fontSize: 12, marginTop: 4, display: 'block' }}>Passwords do not match</span>
            )}
            {passwordsMatch && (
              <span style={{ color: '#2e7d32', fontSize: 12, marginTop: 4, display: 'block' }}>Passwords match</span>
            )}
          </div>

          <button
            type="submit"
            className="btn-login"
            disabled={loading || !allRulesPassed || !passwordsMatch}
          >
            {loading ? 'Changing Password...' : 'Set New Password & Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ChangePassword;
